// "Sort my to-dos now" on the Projects page (owner ruling 11's Q12(c) of 2026-10-04; escalation 20261005-000818, design
// §2.11; ADR 0018): what the page head offers for the sorting pass over the reader's open to-dos in no project, from the
// list's `sort` reading. Pure; unit-tested under node. The words are 05-content §6's (Pending, Running), the sentences
// whole, plurals by rule (§8); never a disabled button as the only explanation (03-components §2) — the reason stands
// where the control would, as the Assistant's "Refresh now" does (lib/assistant.ts refreshNowOffer).

import type { ProjectSortStateDto } from '../../../shared/api-types.js';
import { GLOBAL_CAPABILITY_LABELS } from '../../../shared/enums.js';
import { formatCount, pluralise } from '../../ui/format.js';

/** The button's label, verbatim (design §2.11). */
export const SORT_NOW_LABEL = 'Sort my to-dos now';

const SUMMARIES = GLOBAL_CAPABILITY_LABELS.ai_summaries;
const CONNECT = GLOBAL_CAPABILITY_LABELS.slack_connect;
const ANALYSIS = GLOBAL_CAPABILITY_LABELS.slack_analysis;

/**
 * What stands in the control's place for a reader who holds neither grant path: permission-denied, not empty, not an
 * error (03-components §1: "state that access is restricted and how to request it"). It names only the paths this
 * installation's floors leave standing (`sort.paths`) — the Slack path needs Slack connect AND Slack analysis, which the
 * resolver does not make one imply the other — and ends, as every permission-denied sentence in Calis does, with whom to
 * ask. Whole sentences, one per case (05-content §8). Amends design §2.11's sentence, which named Slack analysis alone and
 * no way to ask (ADR 0018).
 */
export function sortNotGrantedSentence(paths: ProjectSortStateDto['paths']): string {
  const summaries = paths.includes('ai_summaries');
  const slack = paths.includes('slack');
  if (summaries && slack) {
    return `Calis can’t sort your to-dos for you: that needs ${SUMMARIES}, or both ${CONNECT} and ${ANALYSIS}. Ask an administrator to add you to a group that has them.`;
  }
  if (slack) return `Calis can’t sort your to-dos for you: that needs both ${CONNECT} and ${ANALYSIS}. Ask an administrator to add you to a group that has them.`;
  return `Calis can’t sort your to-dos for you: that needs ${SUMMARIES}. Ask an administrator to add you to a group that has it.`;
}

/** Why a run failed, said as the clause after "Calis couldn’t sort your to-dos:" (05-content §4: what happened, and why). */
const FAILED_WHY: Readonly<Record<string, string>> = {
  ai_invalid_output: 'the AI service returned an answer Calis could not use',
  ai_unavailable: 'the AI service could not answer',
};

/**
 * The latest run that sorted nothing, as the line in the control says it (05-content §6: Failed and Skipped, never
 * Completed): a failure with its reason, or a Skip with why. The button beside it is the retry.
 */
export function sortOutcomeText(outcome: NonNullable<ProjectSortStateDto['lastOutcome']>): string {
  if (outcome.status === 'skipped') {
    return outcome.code === 'project_sort_capped'
      ? 'Skipped · Calis had already sorted your to-dos as often as it can today. You can try again tomorrow.'
      : 'Skipped · nothing was left to sort: every open to-do of yours older than two days was in a project.';
  }
  const why = FAILED_WHY[outcome.code ?? ''] ?? 'something went wrong on our side';
  return `Failed · Calis couldn’t sort your to-dos: ${why}.`;
}

/** The status line between a press and the next reading: the page does not yet know where the job stands. */
export const SORT_ASKING_TEXT = 'Pending · asking Calis to sort your to-dos';

/** The last pass, as the muted line beneath the control says it: its time and its counts — never a word of a to-do. */
export interface SortLastLine {
  at: string;
  /** "7 of 12 to-dos filed". */
  counts: string;
}

export type SortNowOffer =
  /** The pass never runs here: nothing is rendered. */
  | { kind: 'off' }
  /** The reader holds neither grant path: a sentence in the control's place, saying what to ask for. */
  | { kind: 'not_granted'; text: string }
  /** Their job is under way: the status, no button. */
  | { kind: 'running'; text: string; last: SortLastLine | null }
  /** A job of theirs waits: the status beside the button, which brings it forward. */
  | { kind: 'pending'; text: string; last: SortLastLine | null }
  /** A sorting call was made a moment ago: the reason in the control's place. */
  | { kind: 'cooldown'; text: string; until: string; last: SortLastLine | null }
  /** The button; beneath it, the latest run that sorted nothing (`outcome`, with when) and the last pass. */
  | { kind: 'ready'; last: SortLastLine | null; outcome: { text: string; at: string } | null };

/** "7 of 12 to-dos filed" — the count of the to-dos sent carries the plural (05-content §8). */
export function sortCountsText(last: { sent: number; filed: number }): string {
  return `${formatCount(last.filed)} of ${pluralise(last.sent, { one: '# to-do', other: '# to-dos' })} filed`;
}

function lastLine(sort: ProjectSortStateDto): SortLastLine | null {
  return sort.last === null ? null : { at: sort.last.at, counts: sortCountsText(sort.last) };
}

/**
 * What the control offers for the reading `sort` at `now` (design §2.11): the button (`ready`), the button with the
 * pending line (`pending` — a press brings the queued job forward, as a queued brief is), the running line (`running`),
 * the cooldown sentence (`cooldown`), the permission sentence (`not_granted`), or nothing (`off`). The cooldown is read
 * against `now`, so a reading that has gone stale offers the button again.
 */
export function sortNowOffer(sort: ProjectSortStateDto, now: Date): SortNowOffer {
  if (sort.state === 'off') return { kind: 'off' };
  if (sort.state === 'not_granted') return { kind: 'not_granted', text: sortNotGrantedSentence(sort.paths) };
  const last = lastLine(sort);
  const job = sort.job;
  if (sort.state === 'running' || job?.status === 'running') {
    return { kind: 'running', text: (job?.attempts ?? 0) > 1 ? 'Running · the first attempt failed; Calis is trying again' : 'Running · Calis is sorting your to-dos', last };
  }
  const pending = job?.status === 'queued' ? (job.attempts > 0 ? 'Pending · the first attempt failed; Calis will try again shortly' : 'Pending · Calis will sort your to-dos shortly') : null;
  const until = sort.cooldownUntil;
  if (until !== null && Date.parse(until) > now.getTime()) {
    const minutes = Math.max(1, Math.ceil((Date.parse(until) - now.getTime()) / 60_000));
    // A job already waiting is still said, so the same queue reads the same before and after the cooldown; a run that
    // failed is never said to have sorted (05-content §6).
    const text = pending
      ? pluralise(minutes, { one: `${pending} — you can bring it forward in # minute.`, other: `${pending} — you can bring it forward in # minutes.` })
      : sort.lastOutcome?.status === 'failed'
        ? pluralise(minutes, { one: 'Failed · Calis couldn’t sort your to-dos — you can try again in # minute.', other: 'Failed · Calis couldn’t sort your to-dos — you can try again in # minutes.' })
        : pluralise(minutes, { one: 'Sorted a moment ago — you can ask again in # minute.', other: 'Sorted a moment ago — you can ask again in # minutes.' });
    return { kind: 'cooldown', text, until, last };
  }
  if (pending !== null) return { kind: 'pending', text: pending, last };
  return { kind: 'ready', last, outcome: sort.lastOutcome ? { text: sortOutcomeText(sort.lastOutcome), at: sort.lastOutcome.at } : null };
}

/** Whether the page should read the list again soon: while a job of theirs waits or runs (the Assistant's idiom). */
export function sortInFlight(sort: ProjectSortStateDto | null | undefined): boolean {
  return sort !== null && sort !== undefined && (sort.state === 'pending' || sort.state === 'running' || sort.job !== null);
}
